import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import uuid from 'uuid';
import { useHistory } from 'react-router-dom';
import { ACTIONS, STATUS } from 'react-joyride';

import { useViewport } from 'contexts/viewport';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import feedbackService from 'services/feedback';
import { TYPES } from 'utils/constants';
import { debounceRequest } from 'utils/helper';
import theme from 'shell/themes';
import TourTooltip from 'components/organisms/Tour/TourTooltip';
import GuidedTourStep1 from 'utils/assetFiles/svgs/GuidedTourStep1';
import GuidedTourStep2 from 'utils/assetFiles/svgs/GuidedTourStep2';
import GuidedTourStep3 from 'utils/assetFiles/svgs/GuidedTourStep3';
import GuidedTourStep5 from 'utils/assetFiles/svgs/GuidedTourStep5';
import GuidedTourStep7 from 'utils/assetFiles/svgs/GuidedTourStep7';

const TourContext = createContext();
const useTour = () => useContext(TourContext);

const initialTaskList = {
    all: {},
    toDo: [],
    inProgress: [],
    underReview: [],
    completed: [],
    modifiedOn: 0,
};
const initialSubtaskList = {};
const initialFeedbackList = {};
const initialFileList = {};
const initialSharedUrlList = {};

const taskListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialTaskList;
        }
        case 'SET_TASKLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_TASKS': {
            const { tasks } = payload;
            return {
                ...state,
                all: tasks
                    .filter(({ active }) => active)
                    .reduce((acc, cur) => {
                        return {
                            ...acc,
                            [cur.id]: cur,
                        };
                    }, {}),
            };
        }
        case 'FETCH_TASK_MAP': {
            const { taskMap } = payload;
            const { toDo, inProgress, underReview, completed, modified_on } = taskMap;
            return {
                ...state,
                toDo,
                inProgress,
                underReview,
                completed,
                modifiedOn: modified_on,
            };
        }
        case 'CREATE_TASK': {
            const { task } = payload;
            return {
                ...state,
                all: {
                    [task.id]: task,
                    ...state.all,
                },
                [task.status]: [task.id, ...state[task.status]],
            };
        }
        case 'UPDATE_TASK': {
            const { id, key, value, timestamp } = payload;
            const task = state.all[id];
            if (!task) return state;
            const currentStatus = task.status;
            const updatedTask = { ...task };
            if (key === 'title') delete updatedTask.isLocal;
            if (key === 'shouldCreateActivityOnBlur') delete updatedTask.shouldCreateActivityOnBlur;
            return {
                ...state,
                all: {
                    ...state.all,
                    [id]: {
                        ...updatedTask,
                        [key]: value,
                        modified_on: timestamp,
                    },
                },
                ...(key === 'status' && value !== currentStatus
                    ? {
                          [currentStatus]: state[currentStatus].filter((x) => x !== id),
                          [value]: [id, ...state[value]],
                      }
                    : {}),
                modifiedOn: key === 'status' ? timestamp : state.modifiedOn,
            };
        }
        case 'DELETE_TASK': {
            const { id } = payload;
            const currentStatus = state.all[id].status;
            return {
                ...state,
                all: {
                    ...state.all,
                    [id]: {
                        ...state.all[id],
                        active: false,
                        modified_on: new Date().getTime(),
                    },
                },
                [currentStatus]: state[currentStatus].filter((x) => x !== id),
            };
        }
        case 'DUPLICATE_TASK': {
            const { task } = payload;
            return {
                ...state,
                all: {
                    ...state.all,
                    [task.id]: { ...task },
                },
                [task.status]: [...state[task.status], task.id],
            };
        }
        case 'MOVE_TASK': {
            const { id, destination, timestamp } = payload;
            const task = state.all[id];
            if (!task) return state;
            let taskMap = {
                toDo: [...state.toDo],
                inProgress: [...state.inProgress],
                underReview: [...state.underReview],
                completed: [...state.completed],
            };
            const isReorder = task.status === destination.droppableId;

            taskMap[task.status] = taskMap[task.status].filter((item) => item !== id);
            if (isReorder) {
                taskMap[task.status].splice(destination.index, 0, id);
            } else {
                taskMap[destination.droppableId].splice(destination.index, 0, id);
            }
            taskMap.modifiedOn = timestamp;

            return {
                ...state,
                all: {
                    ...state.all,
                    [id]: { ...task, status: destination.droppableId, modified_on: timestamp },
                },
                ...taskMap,
            };
        }
        case 'CLEANUP_LOCAL_TASKS': {
            const { ids } = payload;
            const newState = cloneDeep(state);
            ids.forEach((id) => {
                delete newState.all[id];
            });
            return {
                ...newState,
                toDo: newState.toDo.filter((toDoId) => !ids.includes(toDoId)),
                inProgress: newState.inProgress.filter((inProgressId) => !ids.includes(inProgressId)),
                underReview: newState.underReview.filter((underReviewId) => !ids.includes(underReviewId)),
                completed: newState.completed.filter((completedId) => !ids.includes(completedId)),
            };
        }
        default: {
            return state;
        }
    }
};

const subtaskListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialSubtaskList;
        }
        case 'SET_SUBTASKLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_SUBTASKS': {
            const { subtasks } = payload;
            return subtasks.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, {});
        }
        case 'CREATE_SUBTASK': {
            const { subtask } = payload;
            return {
                ...state,
                [subtask.id]: subtask,
            };
        }
        case 'UPDATE_SUBTASK': {
            const { id, key, value } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    [key]: value,
                    modified_on: new Date().getTime(),
                },
            };
        }
        case 'DELETE_SUBTASK': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state,
                    active: false,
                    modified_on: new Date().getTime(),
                },
            };
        }
        default: {
            return state;
        }
    }
};

const feedbackListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialFeedbackList;
        }
        case 'SET_FEEDBACKLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_FEEDBACK': {
            const { feedback, subfeedback } = payload;
            return feedback.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: {
                        ...cur,
                        subfeedback: subfeedback[cur.id]
                            ? subfeedback[cur.id].reduce((_acc, _cur) => {
                                  return {
                                      ..._acc,
                                      [_cur.id]: _cur,
                                  };
                              }, {})
                            : {},
                    },
                };
            }, state);
        }
        case 'ADD_FEEDBACK': {
            const { feedback } = payload;
            return {
                ...state,
                [feedback.id]: {
                    ...feedback,
                    subfeedback: {},
                },
            };
        }
        case 'UPDATE_FEEDBACK': {
            const { id, text } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    text,
                    modified_on: new Date().getTime(),
                },
            };
        }
        case 'DELETE_FEEDBACK': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    active: false,
                },
            };
        }
        case 'ADD_SUBFEEDBACK': {
            const { subfeedback } = payload;
            const { id, parent_id } = subfeedback;
            return {
                ...state,
                [parent_id]: {
                    ...state[parent_id],
                    reply_num: state[parent_id].reply_num + 1,
                    subfeedback: {
                        ...state[parent_id].subfeedback,
                        [id]: subfeedback,
                    },
                },
            };
        }
        case 'UPDATE_SUBFEEDBACK_ID': {
            const { oldId, subfeedback } = payload;
            const { id: newId, parent_id } = subfeedback;
            const subfeedbackParentCopy = cloneDeep(state[parent_id]);
            subfeedbackParentCopy.subfeedback[newId] = subfeedback;
            delete subfeedbackParentCopy.subfeedback[oldId];
            return { ...state, [parent_id]: subfeedbackParentCopy };
        }
        case 'UPDATE_SUBFEEDBACK': {
            const { id, parent_id, text } = payload;
            return {
                ...state,
                [parent_id]: {
                    ...state[parent_id],
                    subfeedback: {
                        ...state[parent_id].subfeedback,
                        [id]: {
                            ...state[parent_id].subfeedback[id],
                            text,
                            modified_on: new Date().getTime(),
                        },
                    },
                },
            };
        }
        case 'DELETE_SUBFEEDBACK': {
            const { id, parent_id } = payload;
            return {
                ...state,
                [parent_id]: {
                    ...state[parent_id],
                    subfeedback: {
                        ...state[parent_id].subfeedback,
                        [id]: {
                            ...state[parent_id].subfeedback[id],
                            active: false,
                        },
                    },
                },
            };
        }
    }
};

const fileListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialFileList;
        }
        case 'SET_FILELIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_FILES': {
            return payload.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, state);
        }
        case 'ADD_FILE': {
            const { file } = payload;
            return {
                ...state,
                [file.id]: file,
            };
        }
        case 'DELETE_FILE': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    active: false,
                },
            };
        }
    }
};

const sharedUrlListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialSharedUrlList;
        }
        case 'SET_SHAREDURLLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_SHAREDURLS': {
            return payload.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, state);
        }
        case 'ADD_SHAREDURL': {
            const { sharedUrl } = payload;
            return {
                ...state,
                [sharedUrl.id]: sharedUrl,
            };
        }
        case 'DELETE_SHAREDURL': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    active: false,
                },
            };
        }
        default:
            return state;
    }
};

const STEPS = {
    notMobile: [
        {
            title: 'Welcome to the Dashboard!',
            description: `This is the page where you’ll get a bird’s eye view of your team's overall progress, any alerts to be aware of, and the status of all tasks.`,
            image: GuidedTourStep1,
            width: 443,
            target: '.dashboard-1',
            placement: 'left',
            options: {
                disableBeacon: true,
            },
        },
        {
            title: 'Track Your Team’s Progress',
            description: `You can also explore helpful analytics such as your teammates’ progress, your task workload, team activity and more.`,
            image: GuidedTourStep2,
            width: 458,
            target: '.dashboard-2',
            placement: 'left',
            options: {
                showSkipButton: false,
            },
        },
        {
            title: 'Welcome to the Task Board!',
            description: `The task board is a visual tool that will help you stay on top of your project and collaborate more effectively with your peers. Take some time now to read about how each column is used.`,
            image: GuidedTourStep3,
            link: {
                text: 'Using the Task Board with Your Team',
                url: 'https://www.youtube.com/watch?v=F2A-9A9y_Fg',
            },
            width: 474,
            placement: 'bottom-end',
            target: '.task-1',
            tooltipProps: { styles: { position: 'absolute', bottom: '45px', right: '0px' } },
            options: {
                disableScrolling: true,
                disableScrollParentFix: true,
                floaterProps: { hideArrow: true, offset: -15 },
            },
        },
        {
            title: 'Tasks vs. Milestones',
            description: `Tasks are the individual units of work that need to be accomplished by you and your team to complete the project. 

        Milestones are specific deliverables set by your instructor for you to complete. They serve as significant checkpoints in the project.`,

            width: 451,
            placement: 'right-start',
            target: '.taskCard',
            options: {
                disableScrolling: true,
                locale: {
                    next: 'Got it',
                },
            },
        },
        {
            title: 'Let’s Create a Task',
            description: `First, select the appropriate column on the board where the task belongs. Let’s create a task in the "To Do" column by clicking on the “+” button.`,
            image: GuidedTourStep5,
            width: 364,
            placement: 'auto',
            target: '.task-3',
            tooltipProps: {
                hideButtons: { next: true, back: false },
            },
            options: {
                spotlightClicks: true,
                hideBackButton: true,
                showSkipButton: false,
            },
        },
        {
            title: 'Enter a Title, and Click the Back Arrow to Save',
            description: `This is where you specify task details so your team knows what needs to be done. For now, just enter a title and save the task by clicking the back arrow.`,
            link: { text: 'How To: Set Up a Task', url: 'https://www.youtube.com/watch?v=IlvkVbhMZrg' },
            width: 364,
            placement: 'left',
            target: '.task-4',
            tooltipProps: {
                hideButtons: { next: true, back: false },
            },
            options: {
                spotlightClicks: true,
                hideBackButton: true,
                showSkipButton: false,
            },
        },
        {
            title: 'Time to Move the Task',
            description: `Remember to move tasks through the different stages as they progress. This will help ensure that the project stays on track.

        Let’s move the task you created: hold and drag it from “To Do” to a different column.`,
            image: GuidedTourStep7,
            width: 448,
            placement: 'bottom-start',
            target: '.task-1',
            tooltipProps: {
                hideButtons: { next: true, back: false },
                styles: { position: 'absolute', bottom: '45px', left: '0px' },
            },
            options: {
                disableOverlay: false,
                disableScrollParentFix: true,
                floaterProps: { hideArrow: true },
                spotlightClicks: true,
                hideBackButton: true,
                showSkipButton: false,
            },
        },
        {
            title: 'Edit, Delete and Duplicate Tasks',
            description: `After you create a task, you can still make changes to it by using this menu. For example, if you're working on a task and realize that it requires more work than you expected, you can adjust the workload accordingly even if after you complete the task.`,
            width: 454,
            placement: 'right',
            target: '#dummy',
            options: {
                locale: {
                    next: 'Got it',
                },
                disableOverlay: false,
            },
        },
        {
            title: 'We’re Here to Help!',
            description: `Remember, if you ever need additional support, the help section is here to provide you with any guidance you might need. You can also take this tour again at any point by clicking “Getting Started.”`,
            width: 429,
            placement: 'right',
            target: '#tour',
            options: {
                locale: {
                    last: 'Sounds good',
                },
            },
        },
    ],
    mobile: [
        {
            title: 'Welcome to the Dashboard!',
            description: `This is the page where you’ll get a bird’s eye view of your team's overall progress, any alerts to be aware of, and the status of all tasks.`,
            image: GuidedTourStep1,
            width: 350,
            target: '.dashboard-1',
            placement: 'auto',
            options: {
                disableBeacon: true,
            },
        },
        {
            title: 'Track Your Team’s Progress',
            description: `You can also explore helpful analytics such as your teammates progress, your task workload, team activity and more.`,
            image: GuidedTourStep2,
            width: 350,
            target: '.dashboard-2',
            placement: 'auto',
            options: {
                showSkipButton: false,
            },
        },
        {
            title: 'Welcome to the Task Board!',
            description: `The task board is a visual tool that will help you stay on top of your project and collaborate more effectively with your peers. Take some time now to read about how each column is used.`,
            image: GuidedTourStep3,
            link: {
                text: 'Using the Task Board with Your Team',
                url: 'https://www.youtube.com/watch?v=F2A-9A9y_Fg',
            },
            width: 350,
            placement: 'bottom-end',
            target: '.task-1',
            tooltipProps: { styles: { position: 'absolute', bottom: '45px', right: '0px' } },
            options: {
                disableScrolling: true,
                floaterProps: { hideArrow: true },
            },
        },
        {
            title: 'Tasks vs. Milestones',
            description: `Tasks are the individual units of work that need to be accomplished by you and your team to complete the project. 

        Milestones are specific deliverables set by your instructor for you to complete. They serve as significant checkpoints in the project.`,

            width: 350,
            placement: 'right-start',
            target: '.taskCard',
            tooltipProps: { styles: { position: 'relative', bottom: '45px' } },
            options: {
                disableScrolling: true,
                floaterProps: { hideArrow: true, offset: -15 },
                locale: {
                    next: 'Got it',
                },
            },
        },
        {
            title: 'Let’s Create a Task',
            description: `First, select the appropriate column on the board where the task belongs. Let’s create a task in the "To Do" column by clicking on the “+” button.`,
            image: GuidedTourStep5,
            width: 350,
            placement: 'auto',
            target: '.task-3',
            tooltipProps: {
                hideButtons: { next: true, back: false },
            },
            options: {
                spotlightClicks: true,
                hideBackButton: true,
                showSkipButton: false,
            },
        },
        {
            title: 'Enter a Title and Change the Status',
            description: `This is where you specify task details so your team knows what needs to be done. For now, just enter a title and change the status from the dropdown.`,
            link: { text: 'How To: Set Up a Task', url: 'https://www.youtube.com/watch?v=IlvkVbhMZrg' },
            width: 350,
            placement: 'left',
            target: '#task-main-section',
            tooltipProps: {
                hideButtons: { next: true, back: false },
            },
            options: {
                spotlightClicks: true,
                hideBackButton: true,
                showSkipButton: false,
            },
        },
        {
            title: 'Click the Back Arrow to Save',
            description: `When you’re happy with the task details, click the back arrow and you will see the task on your task board. `,
            link: { text: 'How To: Set Up a Task', url: 'https://www.youtube.com/watch?v=IlvkVbhMZrg' },
            width: 350,
            placement: 'left',
            target: '#task-back-button',
            tooltipProps: {
                hideButtons: { next: true, back: false },
            },
            options: {
                spotlightClicks: true,
                hideBackButton: true,
                showSkipButton: false,
            },
        },
        {
            title: 'Time to Move the Task',
            description: `Remember to move tasks through the different stages as they progress. This will help ensure that the project stays on track.

        Let’s move the task you created: hold and drag it from “To Do” to a different column.`,
            image: GuidedTourStep7,
            width: 350,
            placement: 'right',
            target: '.task-1',
            tooltipProps: {
                hideButtons: { next: true, back: false },
                styles: { position: 'absolute', bottom: '45px', right: '0px' },
            },
            options: {
                disableOverlay: false,
                disableScrollParentFix: true,
                floaterProps: { hideArrow: true },
                spotlightClicks: true,
                hideBackButton: true,
                showSkipButton: false,
            },
        },
        {
            title: 'Edit, Delete and Duplicate Tasks',
            description: `After you create a task, you can still make changes to it by using this menu. For example, if you're working on a task and realize that it requires more work than you expected, you can adjust the workload accordingly even if after you complete the task.`,
            width: 350,
            placement: 'right',
            target: '#dummy-1',

            options: {
                locale: {
                    next: 'Got it',
                },
                disableOverlay: false,
            },
        },
    ],
};

const TourProvider = ({ ...props }) => {
    const { device, width } = useViewport();
    const { auth, currentUser, updateCurrentUser } = useAuth();
    const { message } = useSnackbar();
    const { currentGroup } = useUser2();
    const history = useHistory();

    const [taskList, dispatchTaskAction] = useReducer(taskListReducer, initialTaskList);
    const [subtaskList, dispatchSubtaskAction] = useReducer(subtaskListReducer, initialSubtaskList);
    const [feedbackList, dispatchFeedbackAction] = useReducer(feedbackListReducer, initialFeedbackList);
    const [fileList, dispatchFileAction] = useReducer(fileListReducer, initialFileList);
    const [sharedUrlList, dispatchSharedUrlAction] = useReducer(sharedUrlListReducer, initialSharedUrlList);

    const [isLoading, setIsLoading] = useState(true);
    const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
    const [taskDrawerContent, setTaskDrawerContent] = useState({ task: {} });
    const [aiFeedback, setAIFeedback] = useState(null);
    const [aiFeedbackOverlay, setAIFeedbackOverlay] = useState({ open: false, aiFeedback: null });
    const [filterSort, setFilterSort] = useState({
        sortKey: 'none',
        workloadFilterKey: [],
        assigneeFilterKey: [],
        dueFilterKey: [],
        searchBarInput: '',
    });
    const [alertDialog, setAlertDialog] = useState({
        visible: false,
        title: '',
        content: '',
        actionButtonText: 'Delete',
        variant: 'destructive',
        onConfirm: () => {},
        onCancel: () => {},
    });

    const resetAlertDialog = () => {
        setAlertDialog((current) => ({
            ...current,
            visible: false,
            onConfirm: () => {},
            onCancel: () => {},
        }));
        setTimeout(() => {
            setAlertDialog((current) => ({
                ...current,
                title: '',
                content: '',
                actionButtonText: '',
            }));
        }, 100);
    };

    const reset = () => {
        if (auth.role === 'instructor') {
            dispatchTaskAction({ type: 'RESET' });
            dispatchSubtaskAction({ type: 'RESET' });
            dispatchFeedbackAction({ type: 'RESET' });
            dispatchFileAction({ type: 'RESET' });
            dispatchSharedUrlAction({ type: 'RESET' });
        }
        setFilterSort({
            sortKey: 'none',
            workloadFilterKey: [],
            assigneeFilterKey: [],
            dueFilterKey: [],
            searchBarInput: '',
        });
    };

    const cleanupLocalTasks = (ids) => {
        dispatchTaskAction({
            type: 'CLEANUP_LOCAL_TASKS',
            payload: { ids },
        });
    };

    const getSort = () => {
        switch (filterSort.sortKey) {
            default:
            case 'none':
                return () => 0;
            case 'closestdue':
                return (taskA, taskB) => taskList.all[taskA].end_on - taskList.all[taskB].end_on;
            case 'furthestdue':
                return (taskA, taskB) => taskList.all[taskB].end_on - taskList.all[taskA].end_on;
            case 'titlea-z':
                return (taskA, taskB) =>
                    taskList.all[taskA].title.toLowerCase() > taskList.all[taskB].title.toLowerCase() ? 1 : -1;
            case 'titlez-a':
                return (taskA, taskB) =>
                    taskList.all[taskA].title.toLowerCase() < taskList.all[taskB].title.toLowerCase() ? 1 : -1;
        }
    };

    const checkPassDueFilter = (dueDate, dueFilter) => {
        const today = moment(new Date());
        today.hour(0);
        today.minute(0);
        today.second(0);
        today.millisecond(0);

        if (dueFilter.includes('today') && dueDate.format('D M YYYY') === today.format('D M YYYY')) {
            return true;
        }
        if (dueFilter.includes('thisweek')) {
            const startOfWeek = today.clone().day(0).add(1, 'day');
            const endOfWeek = today.clone().day(6).add(1, 'day');
            if (dueDate.isSameOrAfter(startOfWeek) && dueDate.isSameOrBefore(endOfWeek)) {
                return true;
            }
        }
        if (dueFilter.includes('nextweek')) {
            const startOfNextWeek = today.clone().add(7, 'days').day(0).add(1, 'day');
            const endOfNextWeek = today.clone().add(7, 'days').day(6).add(1, 'day');
            if (dueDate.isSameOrAfter(startOfNextWeek) && dueDate.isSameOrBefore(endOfNextWeek)) {
                return true;
            }
        }
        if (dueFilter.includes('overdue') && dueDate.toDate() < today.toDate()) {
            return true;
        }

        return false;
    };

    const getTaskById = (id) => {
        return taskList.all[id];
    };

    const getTasks = () => {
        const hasWorkloadFilter = Boolean(filterSort.workloadFilterKey.length);
        const hasAssigneeFilter = Boolean(filterSort.assigneeFilterKey.length);
        const hasDueFilter = Boolean(filterSort.dueFilterKey.length);

        return ['toDo', 'inProgress', 'underReview', 'completed'].reduce((acc, column) => {
            return {
                ...acc,
                [column]: [...taskList[column]].sort(getSort()).map((id) => {
                    const task = taskList.all[id];
                    const passesWorkloadFilter = hasWorkloadFilter
                        ? filterSort.workloadFilterKey.includes(task.workload)
                        : true;
                    const passesAssigneeFilter = hasAssigneeFilter
                        ? filterSort.assigneeFilterKey.includes(task.assignee_account_id)
                        : true;
                    const passesDueFilter = hasDueFilter
                        ? checkPassDueFilter(moment(task.end_on), filterSort.dueFilterKey)
                        : true;
                    const passesSearchBarInput = filterSort.searchBarInput
                        ? task.title?.toLowerCase().includes(filterSort.searchBarInput.toLowerCase())
                        : true;

                    return {
                        ...task,
                        column,
                        show: passesWorkloadFilter && passesAssigneeFilter && passesDueFilter && passesSearchBarInput,
                    };
                }),
            };
        }, {});
    };

    const createTask = (status, milestoneId) => {
        const id = 'dummy';
        const createdTime = new Date().getTime();
        const newTask = {
            id,
            active: true,
            owner_account_id: auth.owner_account_id,
            course_id: auth.course_id,
            group_id: currentGroup.id,
            title: '',
            workload: 'low',
            status,
            created_on: createdTime,
            modified_on: createdTime,
            end_on: createdTime,
            feedback_num: 0,
            file_num: 0,
            isLocal: true,
            shouldCreateActivityOnBlur: true,
            ...(milestoneId && {
                milestone_id: milestoneId,
            }),
        };
        dispatchTaskAction({
            type: 'CREATE_TASK',
            payload: { task: newTask },
        });
        return newTask;
    };

    const updateTask = async (id, key, value, shouldFlush) => {
        const timestamp = new Date().getTime();
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: { id, key, value, timestamp },
        });

        if (shouldFlush) debounceRequest.flush();

        if (key === 'status' && value === 'completed') {
            const subtasks = getSubtasksByTaskId(id);
            subtasks.forEach((subtask) => {
                updateSubtask(subtask.id, 'status', 'completed');
            });
            if (subtasks.length) {
                message.success('Subtasks were completed automatically.');
            }
        }
        if (getTaskById(id).type === TYPES.MILESTONE) {
            if (key === 'status' && value === 'completed') {
                const linkedTasks = Object.values(taskList.all).filter(
                    (task) => task.type !== TYPES.MILESTONE && task.milestone_id === getTaskById(id).milestone_id
                );
                linkedTasks.forEach((linkedTask) => {
                    updateTask(linkedTask.id, 'status', 'completed', true);
                });
                if (linkedTasks.length) {
                    message.success('Linked tasks were completed automatically.');
                }
            }
        }
    };

    const deleteTask = async (id) => {};

    const duplicateTask = (id) => {};

    const moveTask = (id, source, destination) => {
        if (!destination) return;
        if (filterSort.sortKey !== 'none') {
            if (source.droppableId === destination.droppableId) return;
            destination.index = 0;
        }
        const timestamp = new Date().getTime();
        dispatchTaskAction({
            type: 'MOVE_TASK',
            payload: { id, destination, timestamp },
        });
        if (device === 'mobile') {
            handleClickNext();
            const violation = document.getElementById('task-1');
            if (violation) {
                violation.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    };

    const getSubtasksByTaskId = (taskId) => {
        return Object.entries(subtaskList)
            .filter(([, subtask]) => {
                return subtask.active && subtask.parent_id === taskId;
            })
            .map(([id, subtask]) => {
                return subtask;
            });
    };

    const getSubtaskById = (subtaskId) => {
        return subtaskList[subtaskId];
    };

    const createSubtask = (parentId) => {};

    const updateSubtask = async (id, key, value) => {};

    const deleteSubtask = async (id) => {};

    const countSubtasks = (id) => {
        return Object.values(subtaskList).filter((subtask) => {
            return subtask.active && subtask.parent_id === id;
        }).length;
    };

    const fetchFeedbackByTaskId = async (taskId) => {};

    const getFeedbackByTaskId = (taskId) => {
        return Object.values(feedbackList)
            .filter((feedback) => {
                return feedback.parent_id === taskId;
            })
            .reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, {});
    };

    const addFeedback = async (taskId, text, files) => {};

    const updateFeedback = async (id, text) => {
        const currentState = cloneDeep(feedbackList);
        dispatchFeedbackAction({ type: 'UPDATE_FEEDBACK', payload: { id, text } });
    };

    const deleteFeedback = async (id) => {
        const feedbackState = cloneDeep(feedbackList);
        const taskState = cloneDeep(taskList);
        const taskId = feedbackList[id].parent_id;
        dispatchFeedbackAction({ type: 'DELETE_FEEDBACK', payload: { id } });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'feedback_num',
                value: getTaskById(taskId).feedback_num - 1,
            },
        });

        try {
            await feedbackService.deleteFeedback(id, { task_id: taskId });
        } catch (e) {
            console.error(e);
            dispatchFeedbackAction({
                type: 'SET_FEEDBACKLIST_STATE',
                payload: { state: feedbackState },
            });
            dispatchFeedbackAction({
                type: 'SET_TASKLIST_STATE',
                payload: { state: taskState },
            });
        }
    };

    const addSubfeedback = async (taskId, parentId, text, files) => {
        const feedbackState = cloneDeep(feedbackList);
        const taskState = cloneDeep(taskList);
        const createdTime = new Date().getTime();
        const id = uuid.v4();
        const newSubfeedback = {
            id,
            active: true,
            owner_account_id: auth.owner_account_id,
            parent_id: parentId,
            grand_parent_id: taskId,
            text,
            type: TYPES.FEEDBACK,
            created_on: createdTime,
            modified_on: createdTime,
            reply_num: 0,
        };
        dispatchFeedbackAction({
            type: 'ADD_SUBFEEDBACK',
            payload: { subfeedback: newSubfeedback },
        });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'feedback_num',
                value: getTaskById(taskId).feedback_num + 1,
            },
        });
    };

    const updateSubfeedback = async (id, parentId, text) => {
        const currentState = cloneDeep(feedbackList);
        dispatchFeedbackAction({
            type: 'UPDATE_SUBFEEDBACK',
            payload: { id, parent_id: parentId, text },
        });
    };

    const deleteSubfeedback = async (id, parentId) => {
        const feedbackState = cloneDeep(feedbackList);
        const taskState = cloneDeep(taskList);
        const taskId = feedbackList[parentId].parent_id;
        dispatchFeedbackAction({
            type: 'DELETE_SUBFEEDBACK',
            payload: { id, parent_id: parentId },
        });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'feedback_num',
                value: getTaskById(taskId).feedback_num - 1,
            },
        });
    };

    const countFeedback = (taskId) => {
        if (!taskId) return 0;
        return getTaskById(taskId)?.feedback_num || 0;
    };

    const deleteFile = async (id) => {
        const fileState = cloneDeep(fileList);
        const taskState = cloneDeep(taskList);
        const taskId = fileList[id].grand_parent_id;
        dispatchFileAction({ type: 'DELETE_FILE', payload: { id } });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'file_num',
                value: getTaskById(taskId).file_num - 1,
            },
        });
    };

    const countFiles = (taskId) => {
        if (!taskId) return 0;
        return getTaskById(taskId)?.file_num || 0;
    };

    const fetchSharedUrlsByTaskId = async (taskId) => {};

    const createTaskResource = (taskId, resource) => {
        if (resource.data) {
            dispatchFileAction({
                type: 'ADD_FILE',
                payload: {
                    file: {
                        ...resource,
                        active: true,
                        created_on: new Date().getTime(),
                        modified_on: new Date().getTime(),
                        public: true,
                        type: TYPES.TASK,
                        task_id: taskId,
                    },
                },
            });
        } else {
            dispatchSharedUrlAction({
                type: 'ADD_SHAREDURL',
                payload: {
                    sharedUrl: {
                        ...resource,
                        active: true,
                        created_on: new Date().getTime(),
                        modified_on: new Date().getTime(),
                        public: true,
                        type: TYPES.TASK,
                        task_id: taskId,
                    },
                },
            });
        }
    };

    const deleteTaskResource = (resourceType, resourceId) => {
        switch (resourceType) {
            case 'file':
                dispatchFileAction({
                    type: 'DELETE_FILE',
                    payload: { id: resourceId },
                });
                break;
            case 'link':
                dispatchSharedUrlAction({
                    type: 'DELETE_SHAREDURL',
                    payload: { id: resourceId },
                });
                break;
            default:
                break;
        }
    };

    const createActivity = async (data, type, action) => {};

    const setSort = (key) => {
        return setFilterSort((prev) => ({
            ...prev,
            sortKey: key,
        }));
    };

    const toggleWorkloadFilter = (key) => {
        return setFilterSort((prev) => {
            return prev.workloadFilterKey.includes(key)
                ? {
                      ...prev,
                      workloadFilterKey: prev.workloadFilterKey.filter((k) => k !== key),
                  }
                : {
                      ...prev,
                      workloadFilterKey: [...prev.workloadFilterKey, key],
                  };
        });
    };

    const toggleAssigneeFilter = (key) => {
        return setFilterSort((prev) => {
            return prev.assigneeFilterKey.includes(key)
                ? {
                      ...prev,
                      assigneeFilterKey: prev.assigneeFilterKey.filter((k) => k !== key),
                  }
                : {
                      ...prev,
                      assigneeFilterKey: [...prev.assigneeFilterKey, key],
                  };
        });
    };

    const toggleDueFilter = (key) => {
        return setFilterSort((prev) => {
            return prev.dueFilterKey.includes(key)
                ? {
                      ...prev,
                      dueFilterKey: prev.dueFilterKey.filter((k) => k !== key),
                  }
                : {
                      ...prev,
                      dueFilterKey: [...prev.dueFilterKey, key],
                  };
        });
    };

    const onChangeSearchBarInput = (value) => {
        return setFilterSort((prev) => ({
            ...prev,
            searchBarInput: value,
        }));
    };

    const resetFilterSort = () => {
        return setFilterSort((prev) => ({
            ...prev,
            sortKey: 'none',
            workloadFilterKey: [],
            assigneeFilterKey: [],
            dueFilterKey: [],
        }));
    };

    const getCurrentUserCreatedTasks = () => {
        return Object.values(taskList.all)
            .filter(({ active }) => active)
            .filter(({ owner_account_id }) => owner_account_id === auth.owner_account_id);
    };

    const getCurrentUserAssignedTasks = () => {
        return getUserAssignedTasks(auth.owner_account_id);
    };

    const getUserAssignedTasks = (accountId) => {
        return Object.values(taskList.all)
            .filter(({ active }) => active)
            .filter(({ assignee_account_id }) => {
                return accountId ? assignee_account_id === accountId : !assignee_account_id;
            });
    };

    const getUserAssignedSubtasks = (accountId) => {
        return Object.values(subtaskList)
            .filter(({ active }) => active)
            .filter(({ assignee_account_id }) => {
                return accountId ? assignee_account_id === accountId : !assignee_account_id;
            });
    };

    const getOverdueTasks = () => {
        return Object.values(taskList.all)
            .filter(({ active }) => active)
            .filter(({ status }) => status !== 'completed')
            .filter(({ end_on }) => {
                return (
                    end_on < new Date().getTime() &&
                    moment(end_on).format('D M YYYY') !== moment(new Date()).format('D M YYYY')
                );
            });
    };

    const getDummyTasks = () => {
        const taskMap = {
            active: true,
            completed: ['dummy-5'],
            course_id: auth.course_id,
            created_on: 1688765869489,
            group_id: auth.group_id,
            id: 'dummy_task_map',
            inProgress: ['dummy-3'],
            modified_on: 1691513290064,
            toDo: ['dummy-1', 'dummy-2'],
            underReview: ['dummy-4'],
        };

        const activeTasks = [
            {
                active: true,
                course_id: auth.course_id,
                created_on: 1691478710457,
                end_on: new Date().getTime(),
                feedback_num: 0,
                file_num: 0,
                group_id: auth.group_id,
                id: 'dummy-1',
                modified_on: 1691513290056,
                owner_account_id: auth.owner_account_id,
                status: 'toDo',
                title: `Task you haven't started`,
                workload: 'low',
            },
            {
                active: true,
                course_id: auth.course_id,
                created_on: 1688765869470,
                description: '',
                end_on: new Date().getTime(),
                feedback_num: 0,
                file_num: 0,
                group_id: auth.group_id,
                id: 'dummy-2',
                milestone_id: '579430d1-b4c1-4aea-a79a-418d0f9b60f1',
                modified_on: 1689892921373,
                owner_account_id: auth.owner_account_id,
                status: 'toDo',
                submission_type: 'file',
                title: 'Milestone #1',
                type: 'milestone',
                workload: 'low',
            },
            {
                active: true,
                course_id: auth.course_id,
                created_on: 1691478710457,
                end_on: new Date().getTime(),
                feedback_num: 0,
                file_num: 0,
                group_id: auth.group_id,
                id: 'dummy-3',
                modified_on: 1691513290056,
                owner_account_id: auth.owner_account_id,
                status: 'inProgress',
                title: `Task you're working on`,
                workload: 'medium',
            },
            {
                active: true,
                course_id: auth.course_id,
                created_on: 1691478710457,
                end_on: new Date().getTime(),
                feedback_num: 0,
                file_num: 0,
                group_id: auth.group_id,
                id: 'dummy-4',
                modified_on: 1691513290056,
                owner_account_id: auth.owner_account_id,
                status: 'underReview',
                title: `Task you're revising`,
                workload: 'high',
            },
            {
                active: true,
                course_id: auth.course_id,
                created_on: 1691478710457,
                end_on: new Date().getTime(),
                feedback_num: 0,
                file_num: 0,
                group_id: auth.group_id,
                id: 'dummy-5',
                modified_on: 1691513290056,
                owner_account_id: auth.owner_account_id,
                status: 'completed',
                title: `Task you've finished`,
                workload: 'high',
            },
        ];
        const tasks = activeTasks.filter((task) => !task.parent_id);
        const subtasks = activeTasks.filter((task) => task.parent_id);
        return { tasks, subtasks, taskMap };
    };

    useEffect(() => {
        if (!isTaskDrawerOpen) {
            const localTaskIds = Object.values(taskList.all)
                .filter((task) => task.isLocal)
                .map(({ id }) => id);
            cleanupLocalTasks(localTaskIds);
        }
    }, [isTaskDrawerOpen]);

    useEffect(() => {
        let isCancelled = false;
        if (auth) {
            if (currentGroup?.id) {
                (async () => {
                    setIsLoading(true);
                    const { tasks, subtasks, taskMap } = getDummyTasks();
                    if (!isCancelled) {
                        dispatchTaskAction({
                            type: 'FETCH_TASKS',
                            payload: { tasks },
                        });
                        dispatchSubtaskAction({
                            type: 'FETCH_SUBTASKS',
                            payload: { subtasks },
                        });
                        dispatchTaskAction({
                            type: 'FETCH_TASK_MAP',
                            payload: { taskMap },
                        });
                    }
                    setIsLoading(false);
                })();
            } else return reset;
        }
        return () => {
            isCancelled = true;
        };
    }, [auth, currentGroup?.id]);

    const [run, setRun] = useState(true);
    const [tourCompleteModalOpen, setTourCompleteModalOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [tourOn, setTourOn] = useState(false);

    const constructSteps = () => {
        const stepKey = device === 'mobile' ? 'mobile' : 'notMobile';
        return STEPS[stepKey].map((step) => {
            return {
                tooltipComponent: (joyRideProps) => (
                    <TourTooltip
                        sx={{ width: step.width, ...step.tooltipProps?.styles }}
                        currentStep={currentStep}
                        numSteps={STEPS[stepKey].length}
                        title={step.title}
                        description={step.description}
                        image={step.image}
                        {...step.tooltipProps}
                        {...joyRideProps}
                    />
                ),
                placement: step.placement,
                target: step.target,
                styles: {
                    options: {
                        arrowColor: theme.palette.ensightful.primary[500],
                    },
                },
                ...step.options,
            };
        });
    };

    const handleClickStart = (event) => {
        event.preventDefault();
        setRun(true);
    };

    const handleClickNext = () => {
        if (currentStep === 4) {
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
            }, 400);
        } else {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleJoyrideCallback = (data) => {
        const { action, index, status, step, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
        const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        if (type === 'step:after' && action === 'next' && index === 1) {
            history.push('tasks');
        }
        if (type === 'step:after' && action === 'prev' && index === 2) {
            history.push('dashboard');
        }
        if (type === 'step:after' && index === 7) {
            setRun(false);
            return setTimeout(() => {
                setRun(true);
                setCurrentStep(nextStepIndex);
            }, 400);
        } else if (type === 'step:after') {
            setCurrentStep(nextStepIndex);
        }

        if (finishedStatuses.includes(status)) {
            setRun(false);
            setTourCompleteModalOpen(true);
        }
    };

    const handleCloseCompletionModal = () => {
        setTourCompleteModalOpen(false);
        setRun(false);
        updateCurrentUser({ tour: { ...currentUser.tour, guided_tour: true } });
        setTourOn(false);
        history.push('/dashboard');
    };

    const handleReset = () => {
        setTourCompleteModalOpen(false);
        setCurrentStep(0);
        setRun(true);
        setTourOn(true);
        const { tasks, subtasks, taskMap } = getDummyTasks();
        dispatchTaskAction({
            type: 'FETCH_TASKS',
            payload: { tasks },
        });
        dispatchSubtaskAction({
            type: 'FETCH_SUBTASKS',
            payload: { subtasks },
        });
        dispatchTaskAction({
            type: 'FETCH_TASK_MAP',
            payload: { taskMap },
        });
        history.push('/dashboard');
    };

    useEffect(() => {
        const dummyTask = getTaskById('dummy');
        if (device === 'mobile' && dummyTask) {
            if (dummyTask.title && dummyTask.status !== 'toDo') {
                handleClickNext();
            }
        }
    }, [taskList]);

    useEffect(() => {
        if (auth.role === 'student' && !currentUser?.tour?.guided_tour) {
            setRun(true);
            setTourOn(true);
        } else {
            setTourOn(false);
            setRun(false);
        }
    }, [currentUser]);

    useEffect(() => {
        if (tourOn) {
            if (history?.location?.pathname === '/tasks') {
                setCurrentStep(2);
            } else {
                setCurrentStep(0);
            }
        }
    }, [tourOn, history?.location]);

    useEffect(() => {
        if (tourOn && run) {
            window.dispatchEvent(new Event('resize'));
            setRun(false);

            setTimeout(() => {
                setRun(true);
            }, [200]);
        }
    }, [width]);

    const steps = constructSteps();

    return (
        <TourContext.Provider
            value={{
                isLoading,
                reset,
                // task
                taskList,
                getTaskById,
                getTasks,
                createTask,
                updateTask,
                deleteTask,
                duplicateTask,
                moveTask,
                // subtask
                subtaskList,
                getSubtasksByTaskId,
                getSubtaskById,
                createSubtask,
                updateSubtask,
                deleteSubtask,
                countSubtasks,
                // feedback
                fetchFeedbackByTaskId,
                getFeedbackByTaskId,
                addFeedback,
                updateFeedback,
                deleteFeedback,
                addSubfeedback,
                updateSubfeedback,
                deleteSubfeedback,
                countFeedback,
                // files
                fileList,
                deleteFile,
                countFiles,
                // resources
                sharedUrlList,
                fetchSharedUrlsByTaskId,
                createTaskResource,
                deleteTaskResource,
                // activities
                createActivity,
                // taskboard drawer
                isTaskDrawerOpen,
                setIsTaskDrawerOpen,
                taskDrawerContent,
                setTaskDrawerContent,
                // taskboard filtering and sorting
                filterSort,
                setSort,
                toggleWorkloadFilter,
                toggleAssigneeFilter,
                toggleDueFilter,
                onChangeSearchBarInput,
                resetFilterSort,

                // ai feedback
                aiFeedback,
                setAIFeedback,
                aiFeedbackOverlay,
                setAIFeedbackOverlay,

                // rest
                getCurrentUserCreatedTasks,
                getCurrentUserAssignedTasks,
                getUserAssignedTasks,
                getUserAssignedSubtasks,
                getOverdueTasks,

                // alert dialog
                alertDialog,
                setAlertDialog,
                resetAlertDialog,

                // tour
                currentStep,
                tourOn,
                tourCompleteModalOpen,
                run,
                steps,
                isTaskDrawerOpen,
                setIsTaskDrawerOpen,
                handleJoyrideCallback,
                handleClickStart,
                handleClickNext,
                handleCloseCompletionModal,
                handleReset,
            }}
            {...props}
        />
    );
};

export { useTour };
export default TourProvider;
